import { isJsonObject, Json, JsonObject } from '@se/cp.common';
import { DataBinding, DataBindingPart } from '@se/se.ui.html5';
import { findIndex, last } from 'lodash-es';

import { CpEntityBase, isEntity } from './cp-entity-base';
import { ICpEntity, ICpEntityFactory, ICpEntityList, PropertyType } from './interfaces';

export class CpEntityList extends CpEntityBase implements ICpEntityList {
    private advancedDataBindingKeys: string[] | undefined;
    private properties: ICpEntity[] = [];
    private selectedChild?: ICpEntity;

    constructor(factory: ICpEntityFactory, model: Json[] | ICpEntity[], path: DataBinding, parent?: ICpEntity) {
        super(factory, path, parent);

        this.advancedDataBindingKeys = factory.getAdvancedBindingKeys(path);
        if (this.advancedDataBindingKeys) {
            this.createAdvancedProperties(model, this.advancedDataBindingKeys);
        } else {
            this.createProperties(model);
        }

        this.selectedChild = this.properties[0] || undefined;
    }

    getProperties() {
        return this.properties;
    }

    get activeEntities() {
        return this.properties.filter(
            ((p) => isEntity(p) && !this.factory.isUndoEntity(p)) as (p: any) => p is ICpEntity,
        );
    }

    getEntityIndex(entity: ICpEntity) {
        return findIndex(this.properties, (x) => x === entity);
    }

    get advancedDataBindingKeysList() {
        return this.advancedDataBindingKeys ? [...this.advancedDataBindingKeys] : [];
    }

    // #region CpEntityBase implementation

    get Id() {
        if (isEntity(this.selectedChild)) {
            return this.selectedChild.Id;
        }

        return '';
    }

    forOwn(fn: (key: string, value: PropertyType) => boolean | void): void {
        for (let i = 0; i < this.properties.length; ++i) {
            if (fn(i.toString(), this.properties[i])) {
                break;
            }
        }
    }

    getAllEntities(path: DataBinding): ICpEntity[] | undefined {
        if (!path || path.isEmpty()) {
            return this.activeEntities;
        }

        if (path.root.isAdvanced()) {
            const child = this.getProperty(path.root);
            if (isEntity(child)) {
                return child.getAllEntities(path.next);
            }
            return undefined;
        }

        if (!this.selectedChild) {
            throw new Error(`List at ${this.path} has no selected child.`);
        }

        return this.selectedChild.getAllEntities(path);
    }

    getSelectedEntity(path: DataBinding, createMissingEntities = false): ICpEntity | undefined {
        if (!path || path.isEmpty()) {
            return this.selectedChild;
        }

        if (path.root.isAdvanced()) {
            const child = this.getProperty(path.root);
            if (isEntity(child)) {
                return child.getSelectedEntity(path.next, createMissingEntities);
            }
            return this.createChild(path.root)?.getSelectedEntity(path.next, createMissingEntities);
        }

        if (this.selectedChild) {
            return this.selectedChild.getSelectedEntity(path, createMissingEntities);
        }

        if (createMissingEntities) {
            const child = this.properties.length > 0 ? this.properties[0] : this.createChild();
            const nextPath = path.root.isSimple() ? path : path.next;

            return child?.getSelectedEntity(nextPath, createMissingEntities);
        }

        return undefined;
    }

    getPropertyValue(propertyPath: string): PropertyType {
        const propertyDataBinding = new DataBinding(propertyPath).root;
        if (!propertyDataBinding.isSimple()) {
            return this.getProperty(propertyDataBinding) ?? null;
        }
        if (this.selectedChild) {
            return this.selectedChild.getPropertyValue(propertyPath);
        }

        return null;
    }

    getPropertyPrimitiveValue(propertyPath: string): PropertyType {
        if (this.selectedChild) {
            return this.selectedChild.getPropertyPrimitiveValue(propertyPath);
        }

        return null;
    }

    hasProperty(propertyName: string): boolean {
        return this.selectedChild?.hasProperty(propertyName) ?? false;
    }

    navigateToPath(path: DataBinding, createMissingEntities = false): PropertyType | undefined {
        if (!path || path.isEmpty()) {
            return this;
        }

        const index = path.root;
        const propertyValue = this.getProperty(index);
        if (this.factory.isUndoEntity(propertyValue)) {
            return null;
        }

        const nextPath = index.isSimple() ? path : path.next;
        if (isEntity(propertyValue)) {
            return propertyValue.navigateToPath(nextPath, createMissingEntities);
        }

        if (
            createMissingEntities &&
            !propertyValue &&
            ((index.isSimple() && !this.selectedChild) || !index.isSimple())
        ) {
            const newEntity = this.createChild(index);

            return newEntity?.navigateToPath(nextPath, createMissingEntities);
        }

        if (this.selectedChild) {
            return this.selectedChild.navigateToPath(path, createMissingEntities);
        }

        return undefined;
    }

    // #endregion

    // #region ICpEntity implementation
    addEntityToList(path: DataBinding, value: ICpEntity, isVirtual: boolean = false): void {
        /**@TODO aggiornare i path dei "figli" in modo ricorsivo */
        if (!path || path.isEmpty()) {
            if (!isVirtual && this.advancedDataBindingKeys) {
                if (value.path?.advanced.length !== this.advancedDataBindingKeys.length) {
                    value.path = this.path;
                    for (const key of this.advancedDataBindingKeys) {
                        const advancedPropertyValue = value.getPropertyValue(key);
                        if (advancedPropertyValue !== null && typeof advancedPropertyValue !== 'string') {
                            throw new Error('Cannot add entity to list without advancedDataBinding value.');
                        }
                        value.path = value.path.addAdvanced(advancedPropertyValue as string | null); // Per far felice tsc
                    }
                }
            } else {
                value.path = this.path.addIndex(this.properties.length);

                /*78207 fix per regole con binding all'interno dei pannelli virtuali*/
                value.forOwn((key, propVal) => {
                    if (isEntity(propVal)) {
                        if (value.path?.full.length && propVal.path?.full.length)
                            propVal.path = DataBinding.addParentIndex(value.path, propVal.path);
                    }
                    return false;
                });
            }
            value.parent = this;
            this.properties = this.properties.concat(value);

            value.select();

            return;
        }

        const index = path.root;
        const propertyValue = this.getProperty(index);

        if (isEntity(propertyValue)) {
            return propertyValue.addEntityToList(path.next, value);
        }

        throw new Error(`Entity ${this.path} has no child at index ${index}.`);
    }

    destroy() {
        super.destroy();

        for (const property of this.properties) {
            if (isEntity(property)) {
                property.destroy();
            }
        }
    }

    remove(entity?: ICpEntity): boolean {
        // Caso in cui si vuole svuotare interamente la lista
        if (!entity) {
            return this.removeAllChildren();
        }

        const children = this.properties;
        let removed = false;
        for (let i = 0; i < children.length; ++i) {
            const child = children[i];
            if (child === entity) {
                this.properties[i] = this.factory.makeUndoEntity(entity);
                this.properties = this.properties.slice();

                if (this.selectedChild === entity) {
                    this.selectedChild = last(this.activeEntities);
                }

                removed = true;
                break;
            }

            if (isEntity(child) && child.remove(entity)) {
                removed = true;
                break;
            }
        }

        if (removed && this.selectedChild === entity) {
            this.selectedChild = last(this.activeEntities);
        }

        return removed;
    }

    select(entity?: ICpEntity): void {
        if (entity) {
            const child = this.properties.find((x) => x === entity);
            if (!child) {
                return;
            }

            this.selectedChild = child;
        }

        this.parent?.select(this);
    }

    setPropertyValue(propertyName: string, value: PropertyType, emitEvent?: boolean, autoUpdated?: boolean) {
        if (!this.selectedChild) {
            this.selectedChild = this.createChild();
        }

        if (!this.selectedChild) {
            throw new Error(`List at ${this.path} has no selected child.`);
        }

        this.selectedChild.setPropertyValue(propertyName, value, emitEvent, autoUpdated);
    }

    toJSON(skipAutogeneratedId: boolean = true): Json {
        return this.activeEntities.map((v) => v.toJSON(skipAutogeneratedId));
    }

    // #endregion

    private createProperties(model: Json[] | ICpEntity[]) {
        this.properties = [];
        for (let i = 0; i < model.length; ++i) {
            const property = model[i];

            if (isEntity(property)) {
                property.parent = this;
                property.path = this.path.addIndex(i);
                this.properties.push(property);
            } else {
                this.properties.push(
                    this.factory.makeEntity(property as any as JsonObject, this.path.addIndex(i), this),
                );
            }
        }
    }

    private createAdvancedProperties(model: Json[] | ICpEntity[], advancedDataBindingKeys: string[]) {
        this.properties = [];

        for (const property of model) {
            let childPath = this.path;

            for (const key of advancedDataBindingKeys) {
                const advancedBindingValue = this.getAdvancedProperty(property, key);
                childPath = childPath.addAdvanced(advancedBindingValue ?? null);
            }

            if (isEntity(property)) {
                property.parent = this;
                property.path = childPath;
                this.properties.push(property);
            } else if (isJsonObject(property)) {
                this.properties.push(this.factory.makeEntity(property, childPath, this));
            } else {
                console.error(`Error: discarding non-entity child of ${this.path}`);
            }
        }
    }

    private createChild(dataBindingPart?: DataBindingPart) {
        // Nel caso di databinding indice posiziona la entity nel posto corretto.
        //   altrimenti creala alla fine della lista.
        const index = dataBindingPart?.isIndex() ? +dataBindingPart.value : this.properties.length;
        // Nel caso di databinding avanzato recupera la entity dal dataset, altrimenti dai placeholder.
        const newEntity = dataBindingPart?.isAdvanced()
            ? this.factory.makeEntityFromDataset(this.path, this, ...dataBindingPart.value)
            : this.factory.makeEntityFromPlaceholder(this.path.addIndex(index), this);

        if (!isEntity(newEntity)) {
            return undefined;
        }

        if (dataBindingPart?.isIndex()) {
            // Non è possibile sovrascrivere una entity.
            if (this.properties[index]) {
                throw new Error(`Error: Overwriting entity at ${this.path.addIndex(index)}.`);
            }
            this.properties[index] = newEntity;
        } else {
            this.properties.push(newEntity);
        }

        this.selectedChild = newEntity;

        return newEntity;
    }

    private getProperty(binding: DataBindingPart): PropertyType | undefined {
        if (!binding.isAdvanced()) {
            return this.properties[+binding.value];
        }
        if (!this.advancedDataBindingKeys) {
            return undefined;
        }
        const bindingValues = binding.value;

        for (const child of this.activeEntities) {
            let matches = true;

            for (let i = 0; i < this.advancedDataBindingKeys.length; ++i) {
                const key = this.advancedDataBindingKeys[i];
                const expectedValue = bindingValues[i];
                const actualValue = this.getAdvancedProperty(child, key);
                matches &&= actualValue !== undefined && actualValue === expectedValue;
            }

            if (matches) {
                return child;
            }
        }
    }

    private getAdvancedProperty(child: Json | ICpEntity, key: string): string | null | undefined {
        let childProperty = null;
        if (isEntity(child)) {
            childProperty = child.getPropertyValue(key);
        } else if (isJsonObject(child)) {
            childProperty = child[key];
        } else {
            return undefined;
        }

        let childPropertyId = undefined;
        if (isEntity(childProperty) || isJsonObject(childProperty)) {
            childPropertyId = childProperty.Id?.toString();
        } else if (childProperty === null) {
            childPropertyId = null;
        }

        if (childPropertyId !== null && typeof childPropertyId !== 'string') {
            console.error(`Error: cannot determine advancedBindingValue of child of ${this.path}`);
            return undefined;
        }

        return childPropertyId;
    }

    private removeAllChildren() {
        let hasRemovedSomething = false;
        for (const e of this.activeEntities) {
            if (this.remove(e)) {
                hasRemovedSomething = true;
            }
        }
        return hasRemovedSomething;
    }
}
